import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { ElementType, useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'

import { AuthConfirmLogout } from '@app/components/Auth/AuthConfirmLogout'
import {
  visibleSidebarFooterAtom,
  sidebarDispatchAtom,
  type UserOption,
  sidebarFooterLoadedAtom,
  sidebarFullyLoadedAtom,
} from '@app/state/sidebarAtom'
import { SidebarAtom } from '@app/state/layoutAtom'
import { showClientTsnCsModalAtom, showFeedbackModalAtom } from '@app/state/modalAtom'
import { userAtom } from '@app/hooks/useUserProfile'
import {
  isAuthenticatedAtom,
  isLoadingAuthStateAtom,
  showLogoutDialogAtom,
} from '@app/state/authAtom'
import { useAuth } from '@app/Auth/hooks/useAuth'

import {
  SidebarFooter as ShadSidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { SidebarSkeleton } from '@app/layout/Sidebar/Sidebar.skeleton'
import ClientSwitcher from '../ClientSwitcher'

export const SidebarFooter = () => {
  const { initiateLogin, initiateLogout } = useAuth()

  const isOpen = useAtomValue(SidebarAtom)
  const sidebarDispatch = useSetAtom(sidebarDispatchAtom)
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const setIsLoadingAuth = useSetAtom(isLoadingAuthStateAtom)
  const setShowFeedbackModal = useSetAtom(showFeedbackModalAtom)
  const sidebarFullyLoaded = useAtomValue(sidebarFullyLoadedAtom)
  const setSidebarFooterLoaded = useSetAtom(sidebarFooterLoadedAtom)
  const setShowClientTsnCsModal = useSetAtom(showClientTsnCsModalAtom)
  const isShowClientTsnCsModalOpen = useAtomValue(showClientTsnCsModalAtom)
  const [showLogoutDialog, setShowLogoutDialog] = useAtom(showLogoutDialogAtom)
  const visibleMenuItems: UserOption[] = useAtomValue(visibleSidebarFooterAtom)

  const { data: userProfile, isSuccess: userProfileLoaded } = useAtomValue(userAtom)

  // Preserved exactly from legacy: T&Cs check + footer loaded flag
  useEffect(() => {
    if (userProfile && userProfileLoaded) {
      setSidebarFooterLoaded(true)
    }

    if (userProfile && userProfile.accepted_tsncs === false && !isShowClientTsnCsModalOpen) {
      setShowClientTsnCsModal(true)
    } else if (userProfile && userProfile.accepted_tsncs === true && isShowClientTsnCsModalOpen) {
      setShowClientTsnCsModal(false)
    }
  }, [
    userProfile,
    isShowClientTsnCsModalOpen,
    setShowClientTsnCsModal,
    userProfileLoaded,
    setSidebarFooterLoaded,
  ])

  const handleLogout = useCallback(() => {
    setShowLogoutDialog(false)
    setIsLoadingAuth(true)
    initiateLogout()
  }, [setShowLogoutDialog, initiateLogout, setIsLoadingAuth])

  return (
    <nav aria-label="User options">
      <ShadSidebarFooter className="p-0">
        {isAuthenticated ? (
          <>
            <SidebarSkeleton loading={!sidebarFullyLoaded} rows={4} rowHeight={37} />

            {sidebarFullyLoaded && (
              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {visibleMenuItems.map((item) => (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton asChild tooltip={item.label}>
                          {item.navigateTo ? (
                            <Link to={item.navigateTo}>
                              <span className="flex  shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
                                {(() => {
                                  const Icon = item.icon as ElementType | undefined
                                  return Icon ? <Icon /> : null
                                })()}
                              </span>
                              <span>{item.label}</span>
                            </Link>
                          ) : (
                            <button type="button" onClick={() => sidebarDispatch(item.onClick)}>
                              <span className="flex size-5 shrink-0 items-center justify-start [&>svg]:h-4 [&>svg]:w-4">
                                {(() => {
                                  const Icon = item.icon as ElementType | undefined
                                  return Icon ? <Icon /> : null
                                })()}
                              </span>
                              <span>{item.label}</span>
                            </button>
                          )}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </>
        ) : (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Sign In">
                    <button type="button" onClick={initiateLogin}>
                      <p className="flex size-5 shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
                        loading...
                      </p>
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <div className="mb-3 p-2">
          <ClientSwitcher />
        </div>

        {/* Feedback + version — hidden when collapsed (icon-only mode) */}
        <div className="group-data-[collapsible=icon]:hidden flex items-center justify-between px-2 py-1">
          <Button
            variant="ghost"
            size="sm"
            className="p-0 text-xs text-white/60 hover:text-white hover:bg-transparent"
            onClick={() => setShowFeedbackModal(true)}
          >
            {isOpen ? 'Submit ' : ''}Feedback
          </Button>
          {typeof APP_VERSION !== 'undefined' ? (
            <span className="text-xs text-white/40">v{APP_VERSION}</span>
          ) : null}
        </div>
      </ShadSidebarFooter>

      <AuthConfirmLogout
        open={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={handleLogout}
      />
    </nav>
  )
}

export default SidebarFooter
